import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  OutlinedInput,
  Slider,
} from "@mui/material";
import { 
  Reply, 
  MoreVert, 
  Add, 
  AutoAwesome, 
  Rule,
  TrendingUp,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Delete
} from "@mui/icons-material";
import axios from "axios";
import { useEffect, useState } from "react";
import moment from "moment";
import { hostname, parseError } from "../../utils";

interface ReplyClassification {
  id: string;
  category: string;
  sentiment: string;
  confidence: number;
  replyContent: string;
  replyFrom: string;
  replyDate: string;
  processed: boolean;
  needsHumanReview: boolean;
}

interface ReplyAction {
  type: string;
  enabled?: boolean;
  tagName?: string;
  listId?: string;
  message?: string;
  replyTemplate?: string;
  escalationNote?: string;
}

interface ReplyRule {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  triggerCategories: string[];
  triggerSentiments: string[];
  minimumConfidence: number;
  actions: ReplyAction[];
  triggerCount: number;
}

interface ReplyTemplate {
  id: string;
  name: string;
  category: string;
  subject: string;
  content: string;
  tone: string;
  useCount: number;
  active: boolean;
}

interface ReplyManagementDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  companyId: string;
  eventListener: (event: any) => void;
}

export default function ReplyManagementDialog({
  open,
  onClose,
  getToken,
  companyId,
  eventListener,
}: ReplyManagementDialogProps) {
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [classifications, setClassifications] = useState<ReplyClassification[]>([]);
  const [rules, setRules] = useState<ReplyRule[]>([]);
  const [templates, setTemplates] = useState<ReplyTemplate[]>([]);
  const [analytics, setAnalytics] = useState<any>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [testReply, setTestReply] = useState("");
  const [testResult, setTestResult] = useState<any>(null);
  const [createRuleOpen, setCreateRuleOpen] = useState(false);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [availableSentiments, setAvailableSentiments] = useState<string[]>([]);
  const [availableLists, setAvailableLists] = useState<any[]>([]);
  const [newRule, setNewRule] = useState<{
    name: string;
    description: string;
    triggerCategories: string[];
    triggerSentiments: string[];
    triggerKeywords: string[];
    minimumConfidence: number;
    priority: number;
    actions: ReplyAction[];
  }>({
    name: "",
    description: "",
    triggerCategories: [],
    triggerSentiments: [],
    triggerKeywords: [],
    minimumConfidence: 70,
    priority: 100,
    actions: []
  });
  const [newTemplate, setNewTemplate] = useState<{
    name: string;
    category: string;
    subject: string;
    content: string;
    tone: string;
    useForCategories: string[];
    useForSentiments: string[];
  }>({
    name: "",
    category: "",
    subject: "",
    content: "",
    tone: "professional",
    useForCategories: [],
    useForSentiments: []
  });

  const loadClassifications = async () => {
    const token = await getToken();
    try {
      const res = await axios.get(`${hostname}/sequences/reply-classifications`, {
        params: { companyId, pageSize: 50 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setClassifications(res.data.items);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    }
  };

  const loadRules = async () => {
    const token = await getToken();
    try {
      const res = await axios.get(`${hostname}/sequences/reply-rules`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRules(res.data.items);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    }
  };

  const loadTemplates = async () => {
    const token = await getToken();
    try {
      const res = await axios.get(`${hostname}/sequences/reply-templates`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(res.data);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    }
  };

  const loadAnalytics = async () => {
    const token = await getToken();
    try {
      const res = await axios.get(`${hostname}/sequences/reply-analytics`, {
        params: { companyId, days: 30 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalytics(res.data);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    }
  };

  const loadCategories = async () => {
    const token = await getToken();
    try {
      const res = await axios.get(`${hostname}/sequences/reply-management/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableCategories(res.data.categories || []);
      setAvailableSentiments(res.data.sentiments || []);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    }
  };

  const loadLists = async () => {
    const token = await getToken();
    try {
      const res = await axios.get(`${hostname}/lists`, {
        params: { companyId, minimal: true },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableLists(res.data.items || []);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    }
  };

  const testClassification = async () => {
    if (!testReply.trim()) return;
    
    setLoading(true);
    const token = await getToken();
    try {
      const res = await axios.post(
        `${hostname}/sequences/reply-classifications/test-classify`,
        { content: testReply },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult(res.data);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setLoading(false);
    }
  };

  const toggleRuleStatus = async (ruleId: string, enabled: boolean) => {
    const token = await getToken();
    try {
      await axios.put(
        `${hostname}/sequences/reply-rules/${ruleId}`,
        { companyId, enabled: !enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadRules();
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    }
  };

  const createRule = async () => {
    if (!newRule.name || !newRule.triggerCategories.length || !newRule.actions.length) {
      eventListener({ type: "SET_ERROR", error: "Please fill in all required fields" });
      return;
    }

    // Validate that all actions have required configuration
    const invalidActions = newRule.actions.filter(action => {
      if (!action.type) return true;
      if (action.type === "add_tag" && !action.tagName) return true;
      if (action.type === "move_to_list" && !action.listId) return true;
      if (action.type === "send_notification" && !action.message) return true;
      if (action.type === "auto_reply" && !action.replyTemplate) return true;
      return false;
    });

    if (invalidActions.length > 0) {
      eventListener({ type: "SET_ERROR", error: "Please complete all action configurations" });
      return;
    }

    const token = await getToken();
    try {
      await axios.post(
        `${hostname}/sequences/reply-rules`,
        { ...newRule, companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Reset form and close dialog
      setNewRule({
        name: "",
        description: "",
        triggerCategories: [],
        triggerSentiments: [],
        triggerKeywords: [],
        minimumConfidence: 70,
        priority: 100,
        actions: []
      });
      setCreateRuleOpen(false);
      await loadRules();
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: any } = {
      interested: 'success',
      meeting_request: 'primary',
      positive: 'success',
      not_interested: 'error',
      negative: 'error',
      out_of_office: 'warning',
      unsubscribe: 'error',
      bounce: 'error',
      question: 'info',
      unknown: 'default',
    };
    return colors[category] || 'default';
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return <CheckCircle color="success" fontSize="small" />;
      case 'negative': return <ErrorIcon color="error" fontSize="small" />;
      default: return <Warning color="warning" fontSize="small" />;
    }
  };

  const loadData = async () => {
    if (!open) return;
    setLoading(true);
    await Promise.all([
      loadClassifications(),
      loadRules(),
      loadTemplates(),
      loadAnalytics(),
      loadCategories(),
      loadLists(),
    ]);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [open, tabValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Reply />
          Reply Management
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Tabs
          value={tabValue}
          onChange={(_, value) => setTabValue(value)}
          sx={{ mb: 3 }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Classifications" />
          <Tab label="Rules" />
          <Tab label="Templates" />
          <Tab label="Analytics" />
          <Tab label="Test" />
        </Tabs>

        {/* Classifications Tab */}
        {tabValue === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Recent Reply Classifications
            </Typography>
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Sentiment</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Content Preview</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classifications.map((classification) => (
                    <TableRow key={classification.id}>
                      <TableCell>
                        {moment(classification.replyDate).format("MMM D, h:mm A")}
                      </TableCell>
                      <TableCell>{classification.replyFrom}</TableCell>
                      <TableCell>
                        <Chip 
                          label={classification.category.replace('_', ' ')} 
                          color={getCategoryColor(classification.category)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getSentimentIcon(classification.sentiment)}
                          {classification.sentiment}
                        </Box>
                      </TableCell>
                      <TableCell>{classification.confidence.toFixed(1)}%</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {classification.replyContent}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {classification.processed ? (
                          <Chip label="Processed" color="success" size="small" />
                        ) : classification.needsHumanReview ? (
                          <Chip label="Needs Review" color="warning" size="small" />
                        ) : (
                          <Chip label="Pending" color="default" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Rules Tab */}
        {tabValue === 1 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Reply Rules
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setCreateRuleOpen(true)}>
                Add Rule
              </Button>
            </Box>

            <Grid container spacing={2}>
              {rules.map((rule) => (
                <Grid item xs={12} md={6} key={rule.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {rule.name}
                        </Typography>
                        <Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={rule.enabled}
                                onChange={() => toggleRuleStatus(rule.id, rule.enabled)}
                                size="small"
                              />
                            }
                            label=""
                          />
                          <IconButton size="small">
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </Box>

                      <Typography variant="body2" color="textSecondary" paragraph>
                        {rule.description}
                      </Typography>

                      <Box mb={2}>
                        <Typography variant="caption" color="textSecondary">
                          Triggers on:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                          {rule.triggerCategories.map((category) => (
                            <Chip key={category} label={category} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="textSecondary">
                          Min confidence: {rule.minimumConfidence}%
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Triggered: {rule.triggerCount} times
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Templates Tab */}
        {tabValue === 2 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Reply Templates
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setCreateTemplateOpen(true)}>
                Add Template
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Tone</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Use Count</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>{template.name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={template.category} 
                          color={getCategoryColor(template.category)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{template.tone}</TableCell>
                      <TableCell>{template.subject}</TableCell>
                      <TableCell>{template.useCount}</TableCell>
                      <TableCell>
                        <Chip 
                          label={template.active ? "Active" : "Inactive"} 
                          color={template.active ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Analytics Tab */}
        {tabValue === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Reply Analytics (Last 30 Days)
            </Typography>
            
            <Grid container spacing={3}>
              {analytics.reply_stats?.map((stat: any) => (
                <Grid item xs={12} md={4} key={`${stat.category}-${stat.sentiment}`}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Chip 
                          label={stat.category} 
                          color={getCategoryColor(stat.category)}
                          size="small"
                        />
                        {getSentimentIcon(stat.sentiment)}
                      </Box>
                      <Typography variant="h4">
                        {stat.count}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Avg confidence: {stat.avg_confidence}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Rule Performance
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rule Name</TableCell>
                      <TableCell>Trigger Count</TableCell>
                      <TableCell>Last Triggered</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.rule_stats?.map((stat: any) => (
                      <TableRow key={stat.rule_name}>
                        <TableCell>{stat.rule_name}</TableCell>
                        <TableCell>{stat.trigger_count}</TableCell>
                        <TableCell>
                          {stat.last_triggered 
                            ? moment(stat.last_triggered).fromNow()
                            : "Never"
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}

        {/* Test Tab */}
        {tabValue === 4 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Test Reply Classification
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Test how the AI would classify a reply without actually processing it.
            </Alert>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reply Content"
              value={testReply}
              onChange={(e) => setTestReply(e.target.value)}
              placeholder="Enter a sample reply to test classification..."
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              onClick={testClassification}
              disabled={!testReply.trim() || loading}
              startIcon={<AutoAwesome />}
              sx={{ mb: 3 }}
            >
              Test Classification
            </Button>

            {testResult && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Classification Results
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Category</Typography>
                      <Chip 
                        label={testResult.category} 
                        color={getCategoryColor(testResult.category)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Sentiment</Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getSentimentIcon(testResult.sentiment)}
                        <Typography>{testResult.sentiment}</Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Confidence</Typography>
                      <Typography variant="h6">{testResult.confidence?.toFixed(1)}%</Typography>
                    </Grid>

                    {testResult.intent && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Detected Intent</Typography>
                        <Typography>{testResult.intent}</Typography>
                      </Grid>
                    )}

                    {testResult.keywords?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Keywords</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {testResult.keywords.map((keyword: string, index: number) => (
                            <Chip key={index} label={keyword} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Create Rule Dialog */}
      <Dialog open={createRuleOpen} onClose={() => setCreateRuleOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Reply Rule</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Rule Name"
                  value={newRule.name}
                  onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={newRule.description}
                  onChange={(e) => setNewRule({...newRule, description: e.target.value})}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Trigger Categories</InputLabel>
                  <Select
                    multiple
                    value={newRule.triggerCategories}
                    onChange={(e) => setNewRule({...newRule, triggerCategories: e.target.value as string[]})}
                    input={<OutlinedInput label="Trigger Categories" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {availableCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category.replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Trigger Sentiments</InputLabel>
                  <Select
                    multiple
                    value={newRule.triggerSentiments}
                    onChange={(e) => setNewRule({...newRule, triggerSentiments: e.target.value as string[]})}
                    input={<OutlinedInput label="Trigger Sentiments" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {availableSentiments.map((sentiment) => (
                      <MenuItem key={sentiment} value={sentiment}>
                        {sentiment}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>
                  Minimum Confidence: {newRule.minimumConfidence}%
                </Typography>
                <Slider
                  value={newRule.minimumConfidence}
                  onChange={(_, value) => setNewRule({...newRule, minimumConfidence: value as number})}
                  min={0}
                  max={100}
                  step={5}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' }
                  ]}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Actions (Configure at least one)
                </Typography>
                
                {/* Action Builder */}
                <Box sx={{ mb: 2 }}>
                  {newRule.actions.map((action, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 1, p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Action Type</InputLabel>
                            <Select
                              value={action.type || ""}
                              onChange={(e) => {
                                const updatedActions = [...newRule.actions];
                                updatedActions[index] = { ...action, type: e.target.value };
                                setNewRule({...newRule, actions: updatedActions});
                              }}
                              label="Action Type"
                            >
                              <MenuItem value="pause_sequence">Pause Sequence</MenuItem>
                              <MenuItem value="add_tag">Add Tag</MenuItem>
                              <MenuItem value="send_notification">Send Notification</MenuItem>
                              <MenuItem value="move_to_list">Move to List</MenuItem>
                              <MenuItem value="auto_reply">Auto Reply</MenuItem>
                              <MenuItem value="escalate">Escalate to Human</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={6}>
                          {/* Configuration based on action type */}
                          {action.type === "add_tag" && (
                            <TextField
                              fullWidth
                              size="small"
                              label="Tag Name"
                              value={action.tagName || ""}
                              onChange={(e) => {
                                const updatedActions = [...newRule.actions];
                                updatedActions[index] = { ...action, tagName: e.target.value };
                                setNewRule({...newRule, actions: updatedActions});
                              }}
                              placeholder="Enter tag name"
                            />
                          )}
                          
                          {action.type === "move_to_list" && (
                            <FormControl fullWidth size="small">
                              <InputLabel>Target List</InputLabel>
                              <Select
                                value={action.listId || ""}
                                onChange={(e) => {
                                  const updatedActions = [...newRule.actions];
                                  updatedActions[index] = { ...action, listId: e.target.value };
                                  setNewRule({...newRule, actions: updatedActions});
                                }}
                                label="Target List"
                              >
                                {availableLists.map((list) => (
                                  <MenuItem key={list.id} value={list.id}>
                                    {list.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                          
                          {action.type === "send_notification" && (
                            <TextField
                              fullWidth
                              size="small"
                              label="Notification Message"
                              value={action.message || ""}
                              onChange={(e) => {
                                const updatedActions = [...newRule.actions];
                                updatedActions[index] = { ...action, message: e.target.value };
                                setNewRule({...newRule, actions: updatedActions});
                              }}
                              placeholder="Enter notification message"
                            />
                          )}
                          
                          {action.type === "auto_reply" && (
                            <TextField
                              fullWidth
                              size="small"
                              label="Reply Template"
                              value={action.replyTemplate || ""}
                              onChange={(e) => {
                                const updatedActions = [...newRule.actions];
                                updatedActions[index] = { ...action, replyTemplate: e.target.value };
                                setNewRule({...newRule, actions: updatedActions});
                              }}
                              placeholder="Reply message template"
                            />
                          )}
                          
                          {action.type === "escalate" && (
                            <TextField
                              fullWidth
                              size="small"
                              label="Escalation Note"
                              value={action.escalationNote || ""}
                              onChange={(e) => {
                                const updatedActions = [...newRule.actions];
                                updatedActions[index] = { ...action, escalationNote: e.target.value };
                                setNewRule({...newRule, actions: updatedActions});
                              }}
                              placeholder="Note for human review"
                            />
                          )}
                          
                          {action.type === "pause_sequence" && (
                            <Typography variant="body2" color="textSecondary">
                              Sequence will be paused for this contact
                            </Typography>
                          )}
                        </Grid>
                        
                        <Grid item xs={2}>
                          <IconButton 
                            onClick={() => {
                              const updatedActions = newRule.actions.filter((_, i) => i !== index);
                              setNewRule({...newRule, actions: updatedActions});
                            }}
                            color="error"
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                  
                  <Button
                    variant="outlined"
                    onClick={() => setNewRule({...newRule, actions: [...newRule.actions, { type: "", enabled: true }]})}
                    startIcon={<Add />}
                    sx={{ mt: 1 }}
                  >
                    Add Action
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Priority (1-1000)"
                  value={newRule.priority}
                  onChange={(e) => setNewRule({...newRule, priority: parseInt(e.target.value) || 100})}
                  inputProps={{ min: 1, max: 1000 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateRuleOpen(false)}>Cancel</Button>
          <Button 
            onClick={createRule} 
            variant="contained"
            disabled={!newRule.name || !newRule.triggerCategories.length || !newRule.actions.length || newRule.actions.some(action => !action.type)}
          >
            Create Rule
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}